import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { getEventThumb } from "@/lib/eventImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Heart, Calendar, Users, Activity, Shield, ArrowRight, 
  ChevronRight, Ribbon, Truck, Play, Camera, DollarSign, Headphones, Radio, Share2
} from "lucide-react";
import { format } from "date-fns";

// Official Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";

// Blue vs Blue branded images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/ww7xw19r_7740016526573477272.jpeg";
const MISSION_IMAGE = "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/gw5nltxv_685162422275311696.jpeg";
const DONATION_IMAGE = "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/aqfim3pc_5143063002655456367.jpeg";

// Mobile Screening Van
const VAN_IMAGE = "https://customer-assets.emergentagent.com/job_396264d1-0774-4ccf-abd8-4f7d3c968f37/artifacts/1brw9hxv_fddfa74e-9a57-493d-b9ea-534d7529deb9.png";

// Early Detection Video
const EARLY_DETECTION_VIDEO = "https://customer-assets.emergentagent.com/job_396264d1-0774-4ccf-abd8-4f7d3c968f37/artifacts/917gxkv1_result_v1.mp4";

// Gallery preview images
const GALLERY_PREVIEW = [
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/aqfim3pc_5143063002655456367.jpeg",
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/mrhmn1tv_462677696162527021.jpeg",
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/mqmgn3ca_2603618804418624027.jpeg",
  "https://customer-assets.emergentagent.com/job_0151f542-6e75-4786-88b8-3312ff27537a/artifacts/gq3ak965_4730695703643224864.jpeg"
];

export default function HomePage() {
  const [stats, setStats] = useState({ supporters: 0, events_held: 0, donations: 0, total_raised: 0, lives_impacted: 0 });
  const [events, setEvents] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [featuredPhotos, setFeaturedPhotos] = useState([]);
  const [partners, setPartners] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [spotlightPartner, setSpotlightPartner] = useState(null);
  const [livePodcast, setLivePodcast] = useState(null);
  const [placeholderVideo, setPlaceholderVideo] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const sharePodcast = (platform) => {
    if (!livePodcast) return;
    const url = `${window.location.origin}/podcast/${livePodcast.podcast_id}`;
    const text = `Watch "${livePodcast.title}" LIVE on Blue vs Blue!`;
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    if (platform === "copy") { navigator.clipboard?.writeText(url); toast.success("Link copied!"); }
    else if (shareUrls[platform]) window.open(shareUrls[platform], "_blank", "width=600,height=400");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, eventsRes, blogsRes, galleryRes, partnersRes, featuredPostsRes, podcastsRes, placeholderRes] = await Promise.all([
        axios.get(`${API}/public/stats`),
        axios.get(`${API}/events?limit=3&upcoming=true`),
        axios.get(`${API}/blog?limit=3&featured=true`),
        axios.get(`${API}/gallery/featured?limit=4`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/community/featured?limit=3`),
        axios.get(`${API}/podcasts?status=live`).catch(() => ({ data: [] })),
        axios.get(`${API}/podcasts/placeholder-video`).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data);
      setBlogs(blogsRes.data);
      setFeaturedPhotos(galleryRes.data);
      setPartners(partnersRes.data);
      setFeaturedPosts(featuredPostsRes.data || []);
      setLivePodcast(podcastsRes.data?.[0] || null);
      setPlaceholderVideo(placeholderRes.data || null);
      
      // Sponsor Spotlight - rotate based on day of week
      const pList = partnersRes.data || [];
      if (pList.length > 0) {
        const dayIndex = new Date().getDay() % pList.length;
        setSpotlightPartner(pList[dayIndex]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to our newsletter!");
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section with Big Logo */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Blue vs Blue at Lucas Oil Stadium" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        
        <div className="relative container-custom py-20">
          <div className="max-w-3xl animate-fade-in-up">
            {/* Big Logo */}
            <div className="mb-8">
              <img 
                src={LOGO_URL} 
                alt="Blue vs Blue Logo" 
                className="h-28 md:h-36 lg:h-44 w-auto drop-shadow-2xl rounded-lg"
                data-testid="hero-logo"
              />
            </div>
            
            <span className="inline-flex items-center gap-2 bg-awareness-blue/20 text-awareness-blue px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Ribbon className="w-4 h-4" />
              501(c)(3) Nonprofit Organization
            </span>
            <h1 className="text-heading-1 text-white text-shadow mb-6">
              Fighting Prostate Cancer Together
            </h1>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl">
              Blue vs Blue is dedicated to bringing prostate cancer awareness to first responders 
              across the United States and to the communities they serve. Early detection is key.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/donate" data-testid="hero-donate-btn">
                <Button className="btn-primary text-lg px-10 py-4">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </Link>
              <Link to="/events" data-testid="hero-events-btn">
                <Button className="bg-white text-deep-blue hover:bg-slate-100 font-semibold text-lg px-10 py-4 rounded-md">
                  View Events
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-50" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="card-stat text-center" data-testid="stat-supporters">
              <Users className="w-10 h-10 text-awareness-blue mx-auto mb-3" />
              <div className="text-4xl font-bold text-deep-blue font-heading">{stats.supporters}+</div>
              <div className="text-slate-600 text-sm uppercase tracking-wide mt-1">Supporters</div>
            </div>
            <div className="card-stat text-center" data-testid="stat-events">
              <Calendar className="w-10 h-10 text-awareness-blue mx-auto mb-3" />
              <div className="text-4xl font-bold text-deep-blue font-heading">{stats.events_held}+</div>
              <div className="text-slate-600 text-sm uppercase tracking-wide mt-1">Events Held</div>
            </div>
            <div className="card-stat text-center" data-testid="stat-raised">
              <div className="h-10 mx-auto mb-3 flex items-center justify-center">
                <img src={VAN_IMAGE} alt="Mobile Screening Unit" className="h-14 w-auto object-contain" style={{ maxWidth: '100px' }} />
              </div>
              <div className="text-4xl font-bold text-deep-blue font-heading">${(stats.total_raised / 1000).toFixed(0)}K+</div>
              <div className="text-slate-600 text-sm uppercase tracking-wide mt-1">Raised</div>
            </div>
            <div className="card-stat text-center" data-testid="stat-lives">
              <Activity className="w-10 h-10 text-awareness-blue mx-auto mb-3" />
              <div className="text-4xl font-bold text-deep-blue font-heading">{stats.lives_impacted}+</div>
              <div className="text-slate-600 text-sm uppercase tracking-wide mt-1">Lives Impacted</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Preview */}
      <section className="section-spacing" data-testid="mission-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Mission</span>
              <h2 className="text-heading-2 text-deep-blue mt-2 mb-6">
                Spreading Awareness, Saving Lives
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Blue vs Blue Prostate Cancer Awareness Organization is dedicated to bringing prostate
                cancer awareness to first responders across the United States and to the communities
                they serve. We believe that by spreading prostate cancer awareness to these people,
                their lives and the lives of their family and community members will be saved.
              </p>
              <Link to="/about" data-testid="mission-learn-more">
                <Button className="btn-outline">
                  Learn More About Us
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={MISSION_IMAGE}
                alt="Blue vs Blue with First Responders"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-deep-blue text-white p-6 rounded-lg shadow-xl">
                <Shield className="w-8 h-8 text-awareness-blue mb-2" />
                <div className="text-2xl font-bold font-heading">501(c)(3)</div>
                <div className="text-sm text-white/80">Tax-Exempt Status</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="section-spacing bg-gradient-to-b from-slate-900 to-deep-blue" data-testid="homepage-podcast">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Podcast</span>
            <h2 className="text-2xl font-bold text-white mt-2">Blue vs Blue Podcast</h2>
            <p className="text-white/60 mt-2 max-w-lg mx-auto">Conversations about prostate cancer awareness, first responder health, and community impact.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            {livePodcast ? (
              <div data-testid="live-podcast-card">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden">
                  <div className="aspect-video bg-gradient-to-br from-deep-blue to-[#0a1e3d] flex flex-col items-center justify-center relative">
                    <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1.5">
                      <Radio className="w-3 h-3 animate-pulse" /> LIVE
                    </div>
                    <img src={LOGO_URL} alt="" className="h-16 rounded-lg mb-4" />
                    <Headphones className="w-12 h-12 text-awareness-blue mb-3" />
                    <h3 className="text-white text-2xl font-bold">{livePodcast.title}</h3>
                    <p className="text-white/60 mt-2">{livePodcast.topic}</p>
                    <Link to={`/podcast/${livePodcast.podcast_id}`} className="mt-4">
                      <Button className="bg-red-500 text-white hover:bg-red-600 text-lg px-8 py-3"><Play className="w-5 h-5 mr-2" /> Watch Live Now</Button>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                  <Button size="sm" variant="outline" className="text-white border-white/30 text-xs" onClick={() => sharePodcast("facebook")}>Facebook</Button>
                  <Button size="sm" variant="outline" className="text-white border-white/30 text-xs" onClick={() => sharePodcast("twitter")}>X / Twitter</Button>
                  <Button size="sm" variant="outline" className="text-white border-white/30 text-xs" onClick={() => sharePodcast("linkedin")}>LinkedIn</Button>
                  <Button size="sm" variant="outline" className="text-white border-white/30 text-xs" onClick={() => sharePodcast("copy")}><Share2 className="w-3 h-3 mr-1" /> Copy</Button>
                </div>
              </div>
            ) : placeholderVideo?.video_url || placeholderVideo?.has_stored ? (
              <div className="rounded-2xl overflow-hidden bg-black">
                <video
                  src={placeholderVideo.has_stored ? `${API.replace('/api', '')}${placeholderVideo.serve_url}` : placeholderVideo.video_url}
                  autoPlay muted loop playsInline controls className="w-full aspect-video object-cover"
                />
              </div>
            ) : (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-12 text-center">
                <Headphones className="w-16 h-16 text-awareness-blue mx-auto mb-4" />
                <h3 className="text-white text-xl font-bold">Coming Soon</h3>
                <p className="text-white/60 mt-2">New episodes dropping regularly. Stay tuned!</p>
              </div>
            )}
            <div className="text-center mt-6">
              <Link to="/podcast"><Button className="btn-secondary">View All Episodes <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      <section className="section-spacing bg-slate-50" data-testid="events-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Upcoming Events</span>
            <h2 className="text-heading-2 text-deep-blue mt-2">Join Us At Our Events</h2>
          </div>
          
          {events.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {events.map((event) => (
                <Link 
                  key={event.event_id} 
                  to={`/events/${event.event_id}`}
                  className="card-event"
                  data-testid={`event-card-${event.event_id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={getEventThumb(event, "https://images.unsplash.com/photo-1614554768702-1b026f460de5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcm9zdGF0ZSUyMGNhbmNlciUyMGF3YXJlbmVzcyUyMGJsdWUlMjByaWJib24lMjBldmVudCUyMGNoYXJpdHklMjBydW58ZW58MHx8fHwxNzc0MDMzMDY3fDA&ixlib=rb-4.1.0&q=85")}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1614554768702-1b026f460de5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcm9zdGF0ZSUyMGNhbmNlciUyMGF3YXJlbmVzcyUyMGJsdWUlMjByaWJib24lMjBldmVudCUyMGNoYXJpdHklMjBydW58ZW58MHx8fHwxNzc0MDMzMDY3fDA&ixlib=rb-4.1.0&q=85"; }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-awareness-blue text-sm font-medium mb-2">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(event.date), "MMMM d, yyyy")}
                    </div>
                    <h3 className="text-heading-3 text-deep-blue mb-2 group-hover:text-awareness-blue transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2">{event.description}</p>
                    <div className="mt-4 flex items-center text-deep-blue font-semibold text-sm">
                      Learn More <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No upcoming events. Check back soon!</p>
            </div>
          )}
          
          <div className="text-center mt-10">
            <Link to="/events" data-testid="view-all-events">
              <Button className="btn-secondary">
                View All Events
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Video Section - Early Detection is Key */}
      <section className="section-spacing" data-testid="video-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Watch & Learn</span>
            <h2 className="text-heading-2 text-deep-blue mt-2">Our Story in Action</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              See the impact of Blue vs Blue through the eyes of our community and first responders.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Early Detection Video - NEW */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="aspect-video">
                <video
                  src={EARLY_DETECTION_VIDEO}
                  title="Early Detection is Key"
                  className="w-full h-full object-cover"
                  controls
                  poster={VAN_IMAGE}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-deep-blue">Early Detection is Key</h3>
                <p className="text-sm text-slate-500 mt-1">Learn why early screening saves lives</p>
              </div>
            </div>

            {/* YouTube Video */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/pmysZhnAoe0"
                  title="Blue vs Blue Mission"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-deep-blue">Blue vs Blue Mission</h3>
                <p className="text-sm text-slate-500 mt-1">Learn about our mission to save lives</p>
              </div>
            </div>
            
            {/* Vimeo Video */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="aspect-video">
                <iframe
                  src="https://player.vimeo.com/video/753417153"
                  title="Our Impact Story"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-deep-blue">Our Impact Story</h3>
                <p className="text-sm text-slate-500 mt-1">See how we're making a difference</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Screening Unit Section - NEW */}
      <section className="section-spacing bg-gradient-to-b from-deep-blue to-[#0a1628]" data-testid="mobile-unit-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <span className="inline-flex items-center gap-2 bg-awareness-blue/20 text-awareness-blue px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Truck className="w-4 h-4" />
                Mobile Screening Unit
              </span>
              <h2 className="text-heading-2 text-white mb-6">
                Help Us Bring Screenings to Your Community
              </h2>
              <p className="text-white/80 text-lg mb-6">
                Our Mobile Screening Unit will travel to fire stations, police departments, and community events 
                across the country, providing free prostate cancer screenings to first responders and their communities.
              </p>
              <p className="text-white/70 mb-8">
                Your donation directly supports our mobile unit campaign, helping us reach more communities 
                and save more lives through early detection.
              </p>
              <Link to="/donate?campaign=camp_051687994bf1" data-testid="mobile-unit-donate-btn">
                <Button className="bg-awareness-blue text-deep-blue hover:bg-[#5aa0d1] font-bold text-lg px-8 py-4 rounded-md">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Donate to Mobile Unit
                </Button>
              </Link>
            </div>
            <div className="flex justify-center">
              <img 
                src={VAN_IMAGE}
                alt="Blue vs Blue Mobile Screening Unit"
                className="max-w-full h-auto rounded-xl shadow-2xl"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Preview */}
      <section className="section-spacing bg-slate-50" data-testid="gallery-preview-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Moments</span>
            <h2 className="text-heading-2 text-deep-blue mt-2">Gallery Highlights</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Browse photos from our events, community outreach, and awareness campaigns.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {(featuredPhotos.length > 0 ? featuredPhotos : GALLERY_PREVIEW.map((url, i) => ({ url, image_id: `fallback-${i}` }))).slice(0, 4).map((photo, idx) => (
              <div key={photo.image_id} className="aspect-square overflow-hidden rounded-lg shadow-md group">
                <img 
                  src={photo.url || `${API}/gallery/image/${photo.image_id}`}
                  alt={photo.title || "Blue vs Blue Event"}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/gallery" data-testid="view-gallery-btn">
              <Button className="btn-secondary">
                <Camera className="w-4 h-4 mr-2" />
                View Full Gallery
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Scrolling Bar */}
      {partners.length > 0 && (
      <section className="py-12 bg-white border-y border-slate-200" data-testid="partners-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Our Partners</span>
            <h3 className="text-xl font-bold text-deep-blue mt-2">Proudly Supported By</h3>
          </div>
          
          {/* Scrolling Partners */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll gap-16 items-center">
              {/* Duplicate for seamless scroll */}
              {[...Array(2)].map((_, setIndex) => (
                <div key={setIndex} className="flex gap-16 items-center shrink-0">
                  {partners.map((p) => (
                    <a
                      key={`${setIndex}-${p.partner_id}`}
                      href={p.website || "#"}
                      target={p.website ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-6 py-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`partner-logo-${p.partner_id}`}
                    >
                      {p.logo_url ? (
                        <img
                          src={`${API.replace('/api', '')}${p.logo_url}`}
                          alt={p.name}
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <Shield className="w-8 h-8 text-deep-blue" />
                      )}
                      <span className="font-semibold text-deep-blue whitespace-nowrap">{p.name}</span>
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Sponsor Spotlight */}
      {spotlightPartner && (
        <section className="py-16 bg-gradient-to-r from-deep-blue to-[#1a3a5c]" data-testid="sponsor-spotlight">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 bg-awareness-blue/20 text-awareness-blue px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Heart className="w-4 h-4" />
                Sponsor Spotlight
              </span>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="w-20 h-20 bg-white rounded-xl mx-auto mb-4 flex items-center justify-center overflow-hidden">
                  {spotlightPartner.logo_url ? (
                    <img src={`${API.replace('/api', '')}${spotlightPartner.logo_url}`} alt={spotlightPartner.name} className="w-full h-full object-contain p-2" />
                  ) : (
                    <Shield className="w-10 h-10 text-deep-blue" />
                  )}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{spotlightPartner.name}</h3>
                <p className="text-white/80 mb-4">
                  Thank you to <strong>{spotlightPartner.name}</strong> for their generous support of Blue vs Blue's mission 
                  to bring prostate cancer awareness to first responders and their communities.
                </p>
                {spotlightPartner.website && (
                  <a href={spotlightPartner.website} target="_blank" rel="noopener noreferrer" 
                    className="inline-flex items-center gap-2 text-awareness-blue hover:underline text-sm font-semibold">
                    Visit {spotlightPartner.name} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
              <p className="text-white/50 text-xs mt-4">Interested in becoming a sponsor? <Link to="/get-involved" className="text-awareness-blue hover:underline">Learn more</Link></p>
            </div>
          </div>
        </section>
      )}

      {/* Donation CTA */}
      <section className="relative py-24" data-testid="donation-cta">
        <div className="absolute inset-0">
          <img 
            src={DONATION_IMAGE}
            alt="Blue awareness ribbon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-deep-blue/90"></div>
        </div>
        <div className="relative container-custom text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 text-awareness-blue px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Heart className="w-4 h-4" />
            Make A Difference
          </span>
          <h2 className="text-heading-2 text-white mb-6 max-w-2xl mx-auto">
            Your Donation Helps Save Lives
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Every contribution supports prostate cancer awareness programs, 
            free screenings, and community outreach efforts.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" data-testid="cta-donate-btn">
              <Button className="bg-awareness-blue text-deep-blue hover:bg-[#5aa0d1] font-bold text-lg px-10 py-4 rounded-md">
                Donate Now
              </Button>
            </Link>
            <Link to="/get-involved">
              <Button className="bg-white/10 text-white hover:bg-white/20 border border-white/30 font-semibold text-lg px-10 py-4 rounded-md">
                Other Ways to Help
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News — hidden until ready */}
      {false && blogs.length > 0 && (
        <section className="section-spacing" data-testid="news-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Latest News</span>
              <h2 className="text-heading-2 text-deep-blue mt-2">Stay Informed</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Link 
                  key={blog.blog_id}
                  to={`/blog/${blog.blog_id}`}
                  className="card-event"
                  data-testid={`blog-card-${blog.blog_id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={blog.image_url || "https://images.pexels.com/photos/7723603/pexels-photo-7723603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-awareness-blue text-xs font-semibold uppercase tracking-wide">{blog.category}</span>
                    <h3 className="text-heading-3 text-deep-blue mt-2 mb-2 group-hover:text-awareness-blue transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2">{blog.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/blog" data-testid="view-all-news">
                <Button className="btn-outline">
                  View All News
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Community Highlights */}
      {featuredPosts.length > 0 && (
        <section className="section-spacing" data-testid="community-highlights">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Community</span>
              <h2 className="text-heading-2 text-deep-blue mt-2">From Our Community</h2>
              <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                Stories, photos, and moments shared by our supporters.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {featuredPosts.slice(0, 3).map((post) => (
                <Link key={post.post_id} to="/community" className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`featured-post-${post.post_id}`}>
                  {post.media?.length > 0 && post.media[0].media_type === "image" && (
                    <div className="aspect-video overflow-hidden">
                      <img src={`${API.replace('/api', '')}${post.media[0].url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-deep-blue flex items-center justify-center text-white text-[10px] font-bold">
                        {post.user_name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      <span className="text-xs font-semibold text-deep-blue">{post.user_name}</span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3">{post.content}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {post.likes_count || 0}</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {post.comments_count || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/community" data-testid="view-community-btn">
                <Button className="btn-secondary">
                  Join the Community
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-16 bg-slate-50" data-testid="newsletter-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-heading-2 text-deep-blue mb-4">Stay Updated</h2>
            <p className="text-slate-600 mb-8">
              Subscribe to our newsletter for the latest news, events, and ways to get involved.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input 
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
                data-testid="newsletter-email"
              />
              <Button type="submit" className="btn-primary" disabled={loading} data-testid="newsletter-submit">
                {loading ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
